import type { ReactNode } from 'react';
import { Home, LogIn, RefreshCw } from 'lucide-react';

import { LoginShell } from './LoginShell';
import { AnakaTriDots } from './primitives/AnakaTriDots';
import { StatusMark } from './primitives/StatusMark';
import { AuthButton } from './primitives/AuthButton';

export type AuthSurfaceVariant = 'refreshing' | 'sso' | 'success' | 'slow' | 'expired' | 'error';

type Accent = 'brand' | 'success' | 'warning' | 'error';
type Loader = 'tri' | 'check' | 'lock';
type Action = 'retry' | 'signin' | 'home';

interface VariantConfig {
  loader: Loader;
  tag: string;
  headline: string;
  message: string;
  meta: string;
  progress?: boolean;
  accent: Accent;
  actions?: Action[];
}

const VARIANTS: Record<AuthSurfaceVariant, VariantConfig> = {
  refreshing: {
    loader: 'tri',
    tag: 'Securing your session',
    headline: 'Just a moment — refreshing your session.',
    message:
      "Your sign-in is still valid. I'm quietly renewing your secure session so you can pick up exactly where you left off.",
    meta: 'session · refreshing · secure',
    progress: true,
    accent: 'brand',
  },
  sso: {
    loader: 'tri',
    tag: "Verifying it's you",
    headline: 'Taking you to your sign-in provider.',
    message:
      "I'm handing you over to your identity provider to confirm it's really you. You'll come straight back here the moment it's done.",
    meta: 'sso · redirecting',
    progress: true,
    accent: 'brand',
  },
  success: {
    loader: 'check',
    tag: 'Verified',
    headline: "You're all set — welcome back.",
    message: "Your session's renewed and secured. Taking you back to right where you were…",
    meta: 'session · active · redirecting',
    progress: true,
    accent: 'success',
  },
  slow: {
    loader: 'tri',
    tag: 'Still working on it',
    headline: 'This is taking longer than usual.',
    message:
      "Your connection or your provider is responding slowly — nothing's wrong on your end. Give it another try, or sign in again to be safe.",
    meta: 'session · retrying',
    accent: 'warning',
    actions: ['retry', 'signin'],
  },
  expired: {
    loader: 'lock',
    tag: 'Session timed out',
    headline: 'Your session has timed out.',
    message:
      "For your security, sessions pause after a while of inactivity. Sign in once more and I'll take you straight back to where you were.",
    meta: 'session · expired · re-auth required',
    accent: 'warning',
    actions: ['signin'],
  },
  error: {
    loader: 'lock',
    tag: "Couldn't sign you in",
    headline: 'Something went wrong signing in.',
    message: 'We hit a snag completing your sign-in. Please try again, or head back home.',
    meta: 'auth · error',
    accent: 'error',
    actions: ['retry', 'home'],
  },
};

const ACCENT: Record<Accent, { fg: string; bg: string; border: string }> = {
  brand: { fg: 'var(--primary)', bg: 'var(--primary-muted)', border: 'color-mix(in srgb, var(--primary) 32%, transparent)' },
  success: { fg: 'var(--success)', bg: 'var(--success-muted)', border: 'color-mix(in srgb, var(--success) 32%, transparent)' },
  warning: { fg: 'var(--warning)', bg: 'var(--warning-muted)', border: 'color-mix(in srgb, var(--warning) 36%, transparent)' },
  error: {
    fg: 'var(--destructive)',
    bg: 'color-mix(in srgb, var(--destructive) 14%, transparent)',
    border: 'color-mix(in srgb, var(--destructive) 32%, transparent)',
  },
};

const ACTION_META: Record<Action, { label: string; icon: typeof LogIn }> = {
  retry: { label: 'Try again', icon: RefreshCw },
  signin: { label: 'Sign in', icon: LogIn },
  home: { label: 'Return home', icon: Home },
};

export interface AuthSurfaceProps {
  variant?: AuthSurfaceVariant;
  /** Overrides the variant's eyebrow tag. */
  tag?: ReactNode;
  /** Overrides the variant's headline. */
  headline?: ReactNode;
  /** Overrides the variant's default body copy (e.g. "Verifying your reset link…"). */
  message?: ReactNode;
  /** Optional footer line under the card (defaults to none). */
  footer?: ReactNode;
  onRetry?: () => void;
  onSignIn?: () => void;
  onHome?: () => void;
}

/**
 * The calm, branded waiting / re-auth surface. One parametric card across six
 * states — refreshing · sso · success · slow · expired · error — with the
 * tri-dots loader, status marks, indeterminate progress, and recovery actions.
 * Reuses LoginShell for the chrome/grid/glow/footer.
 */
export function AuthSurface({ variant = 'refreshing', tag, headline, message, footer, onRetry, onSignIn, onHome }: AuthSurfaceProps) {
  const v = VARIANTS[variant];
  const accent = ACCENT[v.accent];

  const handlers: Record<Action, (() => void) | undefined> = { retry: onRetry, signin: onSignIn, home: onHome };

  return (
    <LoginShell footer={footer}>
      <div
        className="glass-card relative flex w-full max-w-[460px] flex-col items-center gap-5 px-[34px] pb-[34px] pt-10 text-center"
        style={{ borderRadius: 20 }}
      >
        <div aria-hidden={v.loader === 'tri' ? undefined : 'true'}>
          {v.loader === 'tri' ? (
            <AnakaTriDots size={84} />
          ) : (
            <StatusMark kind={v.loader === 'check' ? 'check' : variant === 'error' ? 'error' : 'lock'} size={92} />
          )}
        </div>

        <span
          className="inline-flex items-center gap-[7px] rounded-full px-3 py-[5px] font-mono text-[11px] font-semibold uppercase tracking-[0.12em]"
          style={{ color: accent.fg, background: accent.bg, border: `1px solid ${accent.border}` }}
        >
          {tag ?? v.tag}
        </span>

        <h1 className="m-0 text-[27px] font-extrabold leading-[1.16] tracking-[-0.025em] text-balance text-foreground">
          {headline ?? v.headline}
        </h1>
        <p className="m-0 max-w-[380px] text-pretty text-[14.5px] leading-[1.62] text-muted-foreground">
          {message ?? v.message}
        </p>

        {v.progress && <div className="anaka-prog mt-0.5" style={{ maxWidth: 300 }} role="progressbar" aria-label="Working" />}

        {v.actions && (
          <div className="mt-0.5 flex flex-wrap justify-center gap-2.5">
            {v.actions.map((action, i) => {
              const meta = ACTION_META[action];
              return (
                <AuthButton
                  key={action}
                  variant={i === 0 ? 'primary' : 'ghost'}
                  icon={meta.icon}
                  label={meta.label}
                  onClick={handlers[action]}
                  fullWidth={false}
                  className="px-5"
                />
              );
            })}
          </div>
        )}

        <div className="mt-0.5 inline-flex items-center gap-[7px] font-mono text-[11px] tracking-[0.02em] text-[var(--foreground-subtle)]">
          <span
            className="size-1.5 rounded-full"
            style={{ background: accent.fg, boxShadow: `0 0 0 3px color-mix(in srgb, ${accent.fg} 20%, transparent)` }}
          />
          {v.meta}
        </div>
      </div>
    </LoginShell>
  );
}
